// Conditional formatting — parity with insight-bi's ADV_FORMAT, which it feeds
// to Flexmonster/WebDataRocks as `addCondition({formula, measure, format})`.
//
// ADV_FORMAT is a string of one or more `"<formula>~<json>"` rules joined by
// `$`, e.g.  "#value# > 100~{\"backgroundColor\":\"#fde\"}$#value# < 0~{...}".
// The JSON is a Flexmonster format object (camelCase CSS-ish keys). WebDataRocks
// consumes the same shape, so the Pivot passes the parsed conditions straight
// through; ag-grid has no formula engine, so the Grid evaluates them here — and
// so do the charts (chartAdvFormat.ts), so one ADV_FORMAT reads the same on a
// grid cell and on a bar.
//
// Formula tokens: `#value#` is the value being formatted (the cell, the bar /
// point / slice). `#<COLUMN NAME>#` is another column of the SAME row — e.g.
// `#value# < #TARGET_AMT#` — resolved case-insensitively; a token the row does
// not carry (or a non-numeric value) makes the rule not match. The Pivot hands
// formulas to ProPivot, which knows only `#value#`.
//
// `otherwise` as a whole formula is the catch-all: it applies when no other
// rule of the column matched ("below target → Alert, otherwise → Good").
//
// Format keys: the Flexmonster ones below style a cell. A chart colours its
// mark from `backgroundColor` (else `color`), or from `theme` — a colour theme
// id from visualPalettes.json ("alert", "good", …): the points that rule picks
// out take that theme's colours IN ORDER (the first match its 1st colour, the
// second its 2nd, …).

import type { CSSProperties } from 'react'
import { contrast, parseColor, WHITE } from './color'
import { themePalette } from './visualColors'

export interface FmtCondition {
  formula: string
  format: Record<string, string>
}

/** Parse an ADV_FORMAT string into its conditions (malformed parts skipped). */
export function parseAdvFormat(adv?: string | null): FmtCondition[] {
  if (!adv) return []
  const out: FmtCondition[] = []
  for (const part of String(adv).split('$')) {
    const i = part.indexOf('~')
    if (i < 0) continue
    const formula = part.slice(0, i).trim()
    try {
      const format = JSON.parse(part.slice(i + 1))
      if (formula && format && typeof format === 'object') out.push({ formula, format })
    } catch {
      /* skip a malformed rule rather than break the whole column */
    }
  }
  return out
}

type Row = Record<string, unknown> | null | undefined

function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** A row's value for a column NAME, matched exactly then case-insensitively. */
function rowValue(row: Row, name: string): unknown {
  if (!row) return undefined
  if (name in row) return row[name]
  const lc = name.toLowerCase()
  for (const k of Object.keys(row)) if (k.toLowerCase() === lc) return row[k]
  return undefined
}

/** The column NAMEs a formula reads besides `#value#`. */
export function formulaColumns(formula: string): string[] {
  const out: string[] = []
  for (const m of String(formula || '').matchAll(/#([^#]+)#/g)) {
    const name = m[1].trim()
    if (name && name.toLowerCase() !== 'value' && !out.includes(name)) out.push(name)
  }
  return out
}

// Evaluate a Flexmonster-style numeric formula: comparisons joined by && / ||
// (single level — no nested parens, which covers the common cases). `#value#`
// is `value`; `#NAME#` is that column of `row`.
export function evalFormula(formula: string, value: number, row?: Row): boolean {
  try {
    let unresolved = false
    const expr = String(formula).replace(/#([^#]+)#/g, (_m, token: string) => {
      const name = token.trim()
      const n = name.toLowerCase() === 'value' ? toNumber(value) : toNumber(rowValue(row, name))
      if (n === null) {
        unresolved = true
        return 'NaN'
      }
      return String(n)
    })
    if (unresolved) return false
    return expr.split('||').some((or) => or.split('&&').every(compare))
  } catch (err) {
    console.warn('[visual-engine] ADV_FORMAT formula not evaluated:', formula, err)
    return false
  }
}

const NUM = '(-?\\d+(?:\\.\\d+)?(?:e[+-]?\\d+)?)'
const COMPARE_RE = new RegExp(`^\\s*${NUM}\\s*(>=|<=|==|!=|<>|=|>|<)\\s*${NUM}\\s*$`, 'i')

function compare(expr: string): boolean {
  const m = expr.match(COMPARE_RE)
  if (!m) return false
  const x = parseFloat(m[1])
  const y = parseFloat(m[3])
  switch (m[2]) {
    case '>': return x > y
    case '<': return x < y
    case '>=': return x >= y
    case '<=': return x <= y
    case '==':
    case '=': return x === y
    case '!=':
    case '<>': return x !== y
    default: return false
  }
}

// Flexmonster format keys → React CSS properties.
const STYLE_KEYS: Record<string, keyof CSSProperties> = {
  backgroundColor: 'backgroundColor',
  color: 'color',
  fontFamily: 'fontFamily',
  fontSize: 'fontSize',
  fontWeight: 'fontWeight',
  fontStyle: 'fontStyle',
  textAlign: 'textAlign',
}

/** Map a Flexmonster format object to a React style object (known keys only). */
export function formatToStyle(format: Record<string, string>): CSSProperties {
  const style: Record<string, string> = {}
  for (const [k, prop] of Object.entries(STYLE_KEYS)) {
    if (format[k] != null) style[prop as string] = String(format[k])
  }
  return style as CSSProperties
}

/** The catch-all formula: applies when no other rule matched. */
export const OTHERWISE_FORMULA = 'otherwise'

export function isOtherwise(c: FmtCondition): boolean {
  return String(c.formula || '').trim().toLowerCase() === OTHERWISE_FORMULA
}

/** The merged FORMAT object of every condition that matches (later rules win
 *  per key — the same order Flexmonster applies them), or null. `otherwise`
 *  rules apply only when none of the others matched. */
export function conditionalFormat(
  conditions: FmtCondition[], value: unknown, row?: Row,
): Record<string, string> | null {
  if (!conditions.length) return null
  const n = toNumber(value)
  if (n === null) return null
  let out: Record<string, string> | null = null
  for (const c of conditions) {
    if (isOtherwise(c)) continue
    if (evalFormula(c.formula, n, row)) out = { ...(out || {}), ...c.format }
  }
  if (out) return out
  for (const c of conditions) {
    if (isOtherwise(c)) out = { ...(out || {}), ...c.format }
  }
  return out
}

/**
 * The conditions an engine that knows only `#value#` with fixed colours can
 * run (ProPivot): a `theme` becomes its first colour, and a rule naming
 * another column — or the `otherwise` catch-all — is left out (said once).
 */
export function valueOnlyConditions(conditions: FmtCondition[], el?: Element | null): FmtCondition[] {
  const out: FmtCondition[] = []
  const dropped: string[] = []
  for (const c of conditions) {
    if (isOtherwise(c) || formulaColumns(c.formula).length) { dropped.push(c.formula); continue }
    const format = { ...c.format }
    if (format.theme && !format.backgroundColor) {
      const first = themePalette(format.theme, el ?? null)?.[0]
      if (first) format.backgroundColor = first
    }
    out.push({ formula: c.formula, format })
  }
  if (dropped.length) {
    console.info('[visual-engine] ADV_FORMAT rules this pivot cannot evaluate were skipped:', dropped)
  }
  return out
}

/** Merged style for whichever conditions match a numeric cell value (or null).
 *  `row` lets a formula compare with another column of the same row. A rule
 *  that names a `theme` and no backgroundColor paints the cell with the
 *  theme's first colour (a grid cell is one mark — a chart spreads the theme's
 *  colours over the points the rule picks out; see chartAdvFormat.ts). */
export function conditionalStyle(
  conditions: FmtCondition[], value: unknown, row?: Row, el?: Element | null,
): CSSProperties | null {
  const format = conditionalFormat(conditions, value, row)
  if (!format) return null
  const style = formatToStyle(format) as Record<string, string>
  if (format.theme && !format.backgroundColor) {
    const first = themePalette(format.theme, el ?? null)?.[0]
    if (first) {
      style.backgroundColor = first
      if (!format.color) {
        const rgb = parseColor(first)
        style.color = rgb && contrast(rgb, WHITE) >= 4.5 ? '#ffffff' : '#111827'
      }
    }
  }
  return Object.keys(style).length ? (style as CSSProperties) : null
}
